import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Quote, Loader2, MessageSquarePlus } from 'lucide-react';
import { getApprovedTestimonials } from '../lib/testimonials';

// Fallback static testimonials (used if Firestore is not configured or fails)
const FALLBACK_TESTIMONIALS = [
  {
    name: "Chandra Mohan Yadav",
    role: "VP, Citi Bank",
    location: "Dallas, TX",
    content: "The financial strategies provided helped us secure our retirement and build lasting wealth. The tax-free growth strategies have been absolutely game-changing for our family. Highly recommended!",
    rating: 5,
    highlight: "Retirement Planning"
  },
  {
    name: "Ronit Raj",
    role: "Automation Engineer",
    location: "Austin, Tx", 
    content: "Professional, knowledgeable, and truly cares about our financial success. The comprehensive protection planning gave us complete peace of mind about our family's future.",
    rating: 5,
    highlight: "Personal Protection"
  },
  {
    name: "Nakul Gupta",
    role: "Senior Software Engineer, Meta",
    location: "scremento , CL",
    content: "The personalized approach and attention to detail is unmatched. They helped us create a plan that protects our assets while maximizing growth potential.",
    rating: 5,
    highlight: "Wealth Building"
  },
  {
    name: "Soumya Mishra",
    role: "Engineer, E&Y",
    location: "Dallas, TX",
    content: "Working with GrowAndStayRich has been transformative. Their expertise in tax-efficient strategies has saved us thousands while building our wealth.",
    rating: 5,
    highlight: "Tax Strategies"
  },
  {
    name: "Aditya Kumar",
    role: "Marketing Director",
    location: "Frisco, TX",
    content: "The educational approach and transparency in explaining complex financial concepts made all the difference. We finally understand our financial plan!",
    rating: 5,
    highlight: "Financial Education"
  },
  {
    name: "Robert Martinez",
    role: "Construction Manager",
    location: "Garden City, ID",
    content: "From day one, they've been focused on our long-term success. The comprehensive planning covers everything from protection to growth strategies.",
    rating: 5,
    highlight: "Comprehensive Planning"
  }
];

export default function Testimonials() {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    setIsLoading(true);
    try {
      const firestoreTestimonials = await getApprovedTestimonials({ limitCount: 50, featuredFirst: true });
      
      if (firestoreTestimonials && firestoreTestimonials.length > 0) {
        setTestimonials(firestoreTestimonials);
      } else {
        // If Firestore returns empty (not configured or no approved testimonials), use fallback
        setTestimonials(FALLBACK_TESTIMONIALS);
      }
    } catch (error) {
      console.error('Error loading testimonials:', error);
      // Use fallback on error
      setTestimonials(FALLBACK_TESTIMONIALS);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    // Add a small delay to ensure page is fully rendered
    setTimeout(() => {
      const element = document.querySelector(sectionId);
      if (element) {
        console.log(`Scrolling to ${sectionId}`, element);
        element.scrollIntoView({ 
          behavior: "smooth",
          block: "start",
          inline: "nearest"
        });
      } else {
        console.warn(`Element with id "${sectionId}" not found`);
        // Fallback: try to find any element with similar id
        const allElements = document.querySelectorAll('[id*="booking"]');
        console.log('Elements with "booking" in id:', allElements);
      }
    }, 100);
  };



  return (
    <section id="testimonials" className="relative py-24 bg-[#070E1B] text-white overflow-hidden">
      {/* ── Background Lighting ───────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/4 -right-32 w-[500px] h-[500px] bg-[#C9A84C]/10 rounded-full blur-[150px] animate-pulse-glow" />
        <div className="absolute bottom-10 left-10 w-[500px] h-[500px] bg-[#3D6ACC]/15 rounded-full blur-[150px]" />
        <div 
          className="absolute inset-0 opacity-[0.05]" 
          style={{
            backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs sm:text-sm font-semibold text-[#E8D4A0] tracking-wider uppercase">
            <Quote className="w-4 h-4 text-[#C9A84C]" />
            Client Success Stories
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-playfair tracking-tight">
            Real Families. <span className="text-gradient-gold">Real Wealth Protection</span>.
          </h2>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto font-opensans leading-relaxed">
            Discover how clients achieve absolute peace of mind through custom tax-advantaged strategies, guaranteed 0% downside protection, and Living Benefits.
          </p>
        </div>



        {/* Testimonials Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#C9A84C]" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id || index} 
                className="group relative bg-[#162236]/90 border border-[#C9A84C]/25 hover:border-[#C9A84C]/60 hover:-translate-y-2 transition-all duration-300 rounded-2xl p-8 shadow-2xl backdrop-blur-xl flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-[#C9A84C] text-[#C9A84C]" />
                      ))}
                    </div>
                    {testimonial.highlight && (
                      <span className="px-3 py-1 bg-[#0D1B2A] text-[#E8D4A0] border border-[#C9A84C]/40 text-xs rounded-full font-semibold">
                        {testimonial.highlight}
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm text-slate-200 leading-relaxed italic mb-6">
                    "{testimonial.content}"
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-700/60 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A84C] to-[#8C6D23] text-[#070E1B] font-bold flex items-center justify-center text-sm shadow-md shrink-0">
                    {testimonial.name ? testimonial.name.charAt(0) : "C"}
                  </div>
                  <div>
                    <div className="font-bold text-sm text-white">{testimonial.name}</div>
                    {testimonial.role && (
                      <div className="text-xs text-slate-400">{testimonial.role} {testimonial.location ? `• ${testimonial.location}` : ""}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA Banner */}
        <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-10 sm:p-12 text-center shadow-2xl backdrop-blur-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-[#C9A84C]/10 rounded-full blur-3xl pointer-events-none" />
          <h3 className="text-2xl sm:text-3xl font-bold font-playfair text-white mb-4">Ready to Join Our Success Stories?</h3>
          <p className="text-slate-300 text-base max-w-2xl mx-auto mb-8 leading-relaxed">
            Schedule a confidential 1-on-1 strategy session to discover how custom wealth protection and tax-free growth can elevate your family's future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => scrollToSection("#booking")}
              className="px-8 py-4 bg-gradient-gold-cta text-[#070E1B] rounded-xl font-bold text-base shadow-xl hover:opacity-95 transition-all duration-300"
            >
              Schedule Free Strategy Session
            </button>
            <button 
              onClick={() => navigate('/submit-testimonial')}
              className="px-8 py-4 border border-[#C9A84C]/50 text-[#E8D4A0] rounded-xl font-semibold hover:bg-[#C9A84C]/10 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <MessageSquarePlus className="h-5 w-5 text-[#C9A84C]" />
              Share Your Experience
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}